import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Native Workspace Gallery & Studio View Component for DSH `conversation.view` slot.
 * Fully i18n-reactive (Chinese & English) with modular tabs, multi-dimensional filters,
 * responsive image grid, and placeholder routes.
 */
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { IMAGE_ROUTE, DELETE_ROUTE } from '../shared.js';
import { Image as ImageIcon, SlidersHorizontal, Heart, Search, Copy, Download, FileText, Trash2, X, ChevronLeft, ChevronRight, CheckSquare, Check, AlertTriangle, Sparkles, } from 'lucide-react';
import { getGalleryItems, subscribeGallery, saveGalleryItem, deleteGalleryItem, bulkDeleteGalleryItems, bulkSetFavoriteGalleryItems, toggleFavoriteGalleryItem, isItemInWorkspace, } from './gallery-store.js';
import { StudioView } from './studio-view.js';
import { deselectStudioTlCanvases } from './tl/studio-tl-canvas.js';
import { InspirationView } from './inspiration-view.js';
import { evictAttachmentCache, fetchAttachmentBlob } from './image-cache.js';
import { copyImageBlob, createZipBlob, downloadBlobUrl } from './browser-image-utils.js';
import { conversationRegenerateRequest } from './conversation-regenerate.js';
import { STUDIO_ROUTE } from '../shared.js';
const DICT = {
    zh: {
        // 顶部 Tab
        tabGallery: '图库',
        tabStudio: '工作台',
        tabInspiration: '灵感',
        tabFavorites: '收藏',
        tabCompare: '对比',
        tabTasks: '任务',
        // 筛选工具栏
        filterAllProviders: '全部提供商',
        filterGoogle: 'Google Gemini',
        filterOpenAI: 'OpenAI',
        filterOpenAICompat: 'OpenAI 兼容',
        filterSeedream: '字节 Seedream',
        filterDashScope: '阿里 DashScope',
        filterXAI: 'xAI Grok',
        filterZhipu: '智谱 GLM',
        filterComfyUI: '本地 ComfyUI',
        filterAllModels: '全部模型',
        filterAllRatios: '全部比例',
        searchPlaceholder: '搜索 Prompt、标签…',
        filterCurrentWorkspace: '仅看此工作区',
        filterCurrentWorkspaceHint: '只展示属于当前工作区的生图记录与物理文件',
        sortNewest: '最新生成',
        sortOldest: '最早生成',
        // 状态与提示
        totalCount: '共 {count} 张生成图片',
        emptyTitle: '暂无生图记录',
        emptyDesc: '在对话中让 Agent 生图后，生成的图片会自动收录到这里。',
        favEmptyTitle: '暂无收藏图片',
        favEmptyDesc: '在图库中点击卡片右下角的 ♡ 按钮，即可将喜爱的图片收录到这里。',
        noMatchTitle: '未找到匹配结果',
        noMatchDesc: '尝试更换搜索关键词或调整筛选条件。',
        copiedPrompt: '已复制 Prompt',
        copiedImage: '已复制图片',
        copyFailed: '复制失败',
        favoriteAdded: '已添加到收藏',
        favoriteRemoved: '已取消收藏',
        // 卡片与弹窗操作
        preview: '查看大图',
        download: '下载图片',
        copyImg: '复制图片',
        copyPpt: '复制 Prompt',
        regenerate: '重新生成',
        regenerateTitle: '重新生成图片',
        regenerateHint: '可按需修改 Prompt，基于当前模型和比例在画廊生成一张新图片。',
        confirmRegenerate: '开始生成',
        regenerating: '正在重新生成…',
        regenerateSuccess: '已生成新图片并收录到画廊',
        regenerateSaveFailed: '图片已生成，但保存到图库失败，请重试',
        regenerateFailed: '重新生成失败',
        delete: '从画廊删除',
        confirmDelete: '确定要从画廊中删除这张图片吗？（不会影响原聊天记录）',
        deleted: '已从画廊删除',
        model: '模型',
        prompt: 'Prompt',
        close: '关闭 (Esc)',
        prevImage: '上一张 (←)',
        nextImage: '下一张 (→)',
        // 批量管理与操作
        manage: '批量管理',
        exitManage: '退出选择',
        selectedCount: '已选 {n} 项',
        selectAll: '全选',
        invertSelect: '反选',
        clearSelect: '清空',
        batchFavorite: '批量收藏',
        batchUnfavorite: '批量取消收藏',
        batchFavoritedToast: '已将 {count} 张图片加入收藏',
        batchUnfavoritedToast: '已取消 {count} 张图片的收藏',
        batchFavoriteFailed: '收藏操作失败，请重试',
        batchDownload: '批量下载',
        batchDownloading: '打包中 ({current}/{total})…',
        batchDownloadSingleToast: '已下载 1 张图片',
        batchDownloadZipToast: '已打包下载 {count} 张图片',
        batchDownloadFailed: '批量下载失败，请重试',
        batchDelete: '批量删除',
        batchDeleteTitle: '确认批量删除选中的 {count} 张图片？',
        batchDeleteTitleSingle: '确认从画廊中删除这张图片？',
        batchDeleteDesc: '将从画廊历史记录中移除所选图片。',
        deleteWorkspaceFilesOpt: '同时清理工作区磁盘物理文件（不可恢复）',
        cancel: '取消',
        confirmBatchDelete: '确认删除 ({count})',
        confirmDeleteSingle: '确认删除',
        batchDeletedToast: '已删除 {count} 张图片',
        batchDeletedWithFilesToast: '已删除 {count} 张图片，并清理了 {files} 个工作区文件',
        deleteFailedFileLocked: '已删除 {count} 张图片，但有 {files} 个文件因被系统占用未能删除',
        deleteFailedDatabase: '删除失败：本地数据库操作异常',
        // 时间格式化
        justNow: '刚刚',
        minutesAgo: '{n} 分钟前',
        hoursAgo: '{n} 小时前',
        daysAgo: '{n} 天前',
        // 预留模块占位
        studioTitle: 'AI 图像工作台 (Studio)',
        studioDesc: '工作台模块正在紧锣密鼓开发中。在此你将体验大图精修、变体生成 (Variations)、参数重调并一键将生成结果无缝插回 DSH 正在进行的对话。',
        studioTip: '💡 提示：目前你可以在“图库”中点击任意图片，在弹窗中进行查看、复制与下载。',
        compareTitle: '多模型横向对比 (Compare)',
        compareDesc: '支持单个 Prompt 一键同时调度 Gemini、Seedream、DashScope 及本地 ComfyUI 模型并排生成，直观横评画质与细节。',
        tasksTitle: '异步任务队列 (Tasks)',
        tasksDesc: '集中管理后台批量生图、多模型并发生成与本地 ComfyUI 耗时任务。支持状态追踪、失败重试与执行耗时分析。',
        comingSoonBadge: '即将推出',
    },
    en: {
        // Top Tabs
        tabGallery: 'Gallery',
        tabStudio: 'Studio',
        tabInspiration: 'Inspiration',
        tabFavorites: 'Favorites',
        tabCompare: 'Compare',
        tabTasks: 'Tasks',
        // Filter Toolbar
        filterAllProviders: 'All Providers',
        filterGoogle: 'Google Gemini',
        filterOpenAI: 'OpenAI',
        filterOpenAICompat: 'OpenAI-compatible',
        filterSeedream: 'ByteDance Seedream',
        filterDashScope: 'Aliyun DashScope',
        filterXAI: 'xAI Grok',
        filterZhipu: 'Zhipu GLM',
        filterComfyUI: 'Local ComfyUI',
        filterAllModels: 'All Models',
        filterAllRatios: 'All Ratios',
        searchPlaceholder: 'Search prompt, tags…',
        filterCurrentWorkspace: 'Current workspace only',
        filterCurrentWorkspaceHint: 'Only show images belonging to the active workspace',
        sortNewest: 'Newest first',
        sortOldest: 'Oldest first',
        // States & Toasts
        totalCount: '{count} images total',
        emptyTitle: 'No images generated yet',
        emptyDesc: 'Images generated during conversations will automatically appear here.',
        favEmptyTitle: 'No favorite images yet',
        favEmptyDesc: 'Click the ♡ button on any card in the gallery to collect your favorite images here.',
        noMatchTitle: 'No matching images',
        noMatchDesc: 'Try a different search keyword or adjust filter criteria.',
        copiedPrompt: 'Prompt copied',
        copiedImage: 'Image copied',
        copyFailed: 'Copy failed',
        favoriteAdded: 'Added to favorites',
        favoriteRemoved: 'Removed from favorites',
        // Card & Lightbox Actions
        preview: 'Full Preview',
        download: 'Download',
        copyImg: 'Copy Image',
        copyPpt: 'Copy Prompt',
        regenerate: 'Regenerate',
        regenerateTitle: 'Regenerate Image',
        regenerateHint: 'Edit prompt if needed. A new image will be generated and added to the gallery.',
        confirmRegenerate: 'Generate',
        regenerating: 'Regenerating…',
        regenerateSuccess: 'New image generated and added to gallery',
        regenerateSaveFailed: 'Image generated, but saving to the gallery failed. Please retry.',
        regenerateFailed: 'Regeneration failed',
        delete: 'Delete from gallery',
        confirmDelete: 'Are you sure you want to remove this image from the gallery? (Chat history will not be affected)',
        deleted: 'Deleted from gallery',
        model: 'Model',
        prompt: 'Prompt',
        close: 'Close (Esc)',
        prevImage: 'Previous (←)',
        nextImage: 'Next (→)',
        // Batch Management & Operations
        manage: 'Batch Manage',
        exitManage: 'Done',
        selectedCount: '{n} selected',
        selectAll: 'Select All',
        invertSelect: 'Invert',
        clearSelect: 'Clear',
        batchFavorite: 'Batch Favorite',
        batchUnfavorite: 'Batch Unfavorite',
        batchFavoritedToast: 'Added {count} images to favorites',
        batchUnfavoritedToast: 'Removed {count} images from favorites',
        batchFavoriteFailed: 'Favorite operation failed, please retry',
        batchDownload: 'Batch Download',
        batchDownloading: 'Packaging ({current}/{total})…',
        batchDownloadSingleToast: 'Downloaded 1 image',
        batchDownloadZipToast: 'Packaged & downloaded {count} images',
        batchDownloadFailed: 'Batch download failed, please retry',
        batchDelete: 'Batch Delete',
        batchDeleteTitle: 'Delete {count} selected images?',
        batchDeleteTitleSingle: 'Delete this image from gallery?',
        batchDeleteDesc: 'These images will be removed from your gallery history.',
        deleteWorkspaceFilesOpt: 'Also delete files from workspace disk (cannot be undone)',
        cancel: 'Cancel',
        confirmBatchDelete: 'Delete ({count})',
        confirmDeleteSingle: 'Delete',
        batchDeletedToast: 'Deleted {count} images',
        batchDeletedWithFilesToast: 'Deleted {count} images and cleaned {files} workspace files',
        deleteFailedFileLocked: 'Deleted {count} images, but {files} files could not be deleted (busy/locked)',
        deleteFailedDatabase: 'Failed to delete: local database error',
        // Relative Time
        justNow: 'Just now',
        minutesAgo: '{n}m ago',
        hoursAgo: '{n}h ago',
        daysAgo: '{n}d ago',
        // Route Placeholders
        studioTitle: 'AI Image Studio',
        studioDesc: 'Studio workbench is under active development. Fine-tune prompts, generate variations (2x/4x), and inject images directly into DSH chat.',
        studioTip: '💡 Tip: You can currently click any image in the Gallery to preview, copy, or download it.',
        compareTitle: 'Model Comparison (Compare)',
        compareDesc: 'Side-by-side multi-model benchmarking coming soon. Test Gemini, Seedream, DashScope, and ComfyUI with a single prompt.',
        tasksTitle: 'Task Queue (Tasks)',
        tasksDesc: 'Centralized view for batch generation, asynchronous ComfyUI runs, live progress tracking, and retry controls.',
        comingSoonBadge: 'Coming Soon',
    },
};
/** Format human-readable relative time */
function formatRelativeTime(timestamp, t) {
    const diff = Date.now() - timestamp;
    if (diff < 60_000) {
        return t('justNow');
    }
    if (diff < 3600_000) {
        const mins = Math.max(1, Math.floor(diff / 60_000));
        return t('minutesAgo', { n: String(mins) });
    }
    if (diff < 86400_000) {
        const hours = Math.floor(diff / 3600_000);
        return t('hoursAgo', { n: String(hours) });
    }
    if (diff < 30 * 86400_000) {
        const days = Math.floor(diff / 86400_000);
        return t('daysAgo', { n: String(days) });
    }
    const d = new Date(timestamp);
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}
/** Extract standard aspect ratio for precise filtering */
function getItemRatio(item) {
    if (item.aspectRatio && item.aspectRatio !== 'custom') {
        return item.aspectRatio;
    }
    if (item.output) {
        const ratioMatch = item.output.match(/\b(1:1|16:9|9:16|4:3|3:4|3:2|2:3)\b/);
        if (ratioMatch?.[1])
            return ratioMatch[1];
        const dimMatch = item.output.match(/(\d{3,4})\s*[×x*]\s*(\d{3,4})/);
        if (dimMatch?.[1] && dimMatch[2]) {
            const w = parseInt(dimMatch[1], 10);
            const h = parseInt(dimMatch[2], 10);
            if (w === h)
                return '1:1';
            const approx = w / h;
            if (Math.abs(approx - 16 / 9) < 0.05)
                return '16:9';
            if (Math.abs(approx - 9 / 16) < 0.05)
                return '9:16';
            if (Math.abs(approx - 4 / 3) < 0.05)
                return '4:3';
            if (Math.abs(approx - 3 / 4) < 0.05)
                return '3:4';
            if (Math.abs(approx - 3 / 2) < 0.05)
                return '3:2';
            if (Math.abs(approx - 2 / 3) < 0.05)
                return '2:3';
        }
    }
    return '1:1';
}
/** Format aspect ratio or dimensions for card metadata (e.g., 1024×1024 or 16:9) */
function formatCardMeta(item) {
    if (item.output) {
        const dimMatch = item.output.match(/(\d{3,4})\s*[×x*]\s*(\d{3,4})/);
        if (dimMatch?.[1] && dimMatch[2]) {
            return `${dimMatch[1]}×${dimMatch[2]}`;
        }
    }
    if (item.aspectRatio && item.aspectRatio !== 'custom') {
        return item.aspectRatio;
    }
    if (item.output) {
        const ratioMatch = item.output.match(/\b(1:1|16:9|9:16|4:3|3:4|3:2|2:3)\b/);
        if (ratioMatch?.[1])
            return ratioMatch[1];
    }
    if (item.imageSize) {
        return item.imageSize;
    }
    return '1:1';
}
export const GalleryViewTab = (props) => {
    const { locale, sessionId, useSessions, useWorkspaces, credentialEvents, inSidebar, defaultTab, initialCanvasSurface, useTabInfo } = props;
    const [activeTab, setActiveTab] = useState(defaultTab ?? 'gallery');
    const [studioDraft, setStudioDraft] = useState(undefined);
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedProvider, setSelectedProvider] = useState('all');
    const [selectedRatio, setSelectedRatio] = useState('all');
    const [sortBy, setSortBy] = useState('newest');
    const [onlyCurrentWorkspace, setOnlyCurrentWorkspace] = useState(() => {
        try {
            const stored = localStorage.getItem('dsh-ig-only-current-workspace');
            return stored === null ? true : stored === 'true';
        }
        catch {
            return true;
        }
    });
    const [serverWorkspaces, setServerWorkspaces] = useState([]);
    const [serverActiveRoot, setServerActiveRoot] = useState(null);
    useEffect(() => {
        try {
            localStorage.setItem('dsh-ig-only-current-workspace', String(onlyCurrentWorkspace));
        }
        catch { }
    }, [onlyCurrentWorkspace]);
    // Canvas selection is interaction state tied to this surface: leaving the
    // studio page cancels it so a stale selection can neither resurface on
    // return nor linger in the host mirror. Only fires on hosts that inject the
    // sidebar tab hook; the main-area conversation tab passes none.
    const tabVisible = useTabInfo?.().tab?.visible;
    useEffect(() => {
        if (tabVisible === false)
            deselectStudioTlCanvases();
    }, [tabVisible]);
    useEffect(() => {
        let mounted = true;
        fetch(STUDIO_ROUTE)
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
            if (!mounted || !data)
                return;
            if (Array.isArray(data.workspaces))
                setServerWorkspaces(data.workspaces);
            if (typeof data.workspaceRoot === 'string')
                setServerActiveRoot(data.workspaceRoot);
        })
            .catch(() => { });
        return () => {
            mounted = false;
        };
    }, []);
    const liveWorkspaces = useWorkspaces ? useWorkspaces((s) => s?.items) : null;
    const liveSessions = useSessions ? useSessions((s) => s) : null;
    const currentSessionId = sessionId || liveSessions?.current;
    const activeWorkspace = useMemo(() => {
        const allWs = Array.isArray(liveWorkspaces) && liveWorkspaces.length > 0
            ? liveWorkspaces
            : serverWorkspaces;
        if (allWs.length > 0) {
            if (currentSessionId) {
                const matched = allWs.find((ws) => Array.isArray(ws.sessionIds) && ws.sessionIds.includes(currentSessionId));
                if (matched)
                    return matched;
            }
            if (serverActiveRoot) {
                const normActive = serverActiveRoot.replace(/\\/g, '/').toLowerCase();
                const matched = allWs.find((ws) => ws.path && ws.path.replace(/\\/g, '/').toLowerCase() === normActive);
                if (matched)
                    return matched;
            }
            return allWs[0];
        }
        if (serverActiveRoot) {
            return { workspaceId: 'default', path: serverActiveRoot, title: 'Workspace', sessionIds: [] };
        }
        return null;
    }, [liveWorkspaces, serverWorkspaces, currentSessionId, serverActiveRoot]);
    const [previewItem, setPreviewItem] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const previewUrlRef = useRef(null);
    previewUrlRef.current = previewUrl;
    const [previewBlob, setPreviewBlob] = useState(null);
    const [toast, setToast] = useState(null);
    // In-Gallery Regenerate State
    const [showRegenerateModal, setShowRegenerateModal] = useState(false);
    const [regeneratePrompt, setRegeneratePrompt] = useState('');
    const [isRegenerating, setIsRegenerating] = useState(false);
    const regenerateControllerRef = useRef(null);
    // Batch Management & Shift-Selection State
    const [isManageMode, setIsManageMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const lastSelectedIndexRef = useRef(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteWorkspaceFiles, setDeleteWorkspaceFiles] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);
    // Clear batch selection and shift anchor when switching tabs, searching, or changing filters
    useEffect(() => {
        setSelectedIds(new Set());
        lastSelectedIndexRef.current = null;
    }, [activeTab, search, selectedProvider, selectedRatio, sortBy, onlyCurrentWorkspace]);
    const [lang, setLang] = useState(() => {
        const active = locale?.getSnapshot?.()?.active;
        return active?.startsWith('en') ? 'en' : 'zh';
    });
    useEffect(() => {
        if (!locale?.subscribe)
            return;
        return locale.subscribe(() => {
            const active = locale.getSnapshot?.()?.active;
            setLang(active?.startsWith('en') ? 'en' : 'zh');
        });
    }, [locale]);
    const dict = lang === 'en' ? DICT.en : DICT.zh;
    const t = (key, params) => {
        let text = dict[key] || DICT.zh[key] || key;
        if (params) {
            for (const [k, v] of Object.entries(params)) {
                text = text.replace(`{${k}}`, v);
            }
        }
        return text;
    };
    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => {
            setToast(null);
        }, 2000);
    };
    const useInspirationPrompt = useCallback((prompt) => {
        setStudioDraft(prompt);
        setActiveTab('studio');
    }, []);
    const clearStudioDraft = useCallback(() => setStudioDraft(undefined), []);
    // Hide chat input composer while browsing gallery/studio. Only in the
    // conversation-view variant: the sidebar variant shares the screen with the
    // native conversation, whose composer is the whole point of the split.
    useEffect(() => {
        if (inSidebar)
            return undefined;
        const seat = document.querySelector('[data-composer-seat]');
        if (seat) {
            const prevDisplay = seat.style.display;
            seat.style.display = 'none';
            return () => {
                seat.style.display = prevDisplay;
            };
        }
        return undefined;
    }, [inSidebar]);
    // Load items from IndexedDB
    useEffect(() => {
        let active = true;
        const load = () => {
            void getGalleryItems().then((res) => {
                if (active)
                    setItems(res);
            });
        };
        load();
        const unsubscribe = subscribeGallery(load);
        return () => {
            active = false;
            unsubscribe();
        };
    }, []);
    const [previewLoading, setPreviewLoading] = useState(false);
    const blobCache = useMemo(() => new Map(), []);
    const abortControllerRef = useRef(null);
    // Multi-dimensional filtering and sorting (Provider, Ratio, Search, and Sort)
    const filteredItems = useMemo(() => {
        return items
            .filter((item) => {
            // Tab-level filter (Favorites tab only shows favorited items)
            if (activeTab === 'favorites' && !item.isFavorite) {
                return false;
            }
            // Provider filter
            if (selectedProvider !== 'all' && item.provider !== selectedProvider) {
                return false;
            }
            // Ratio filter
            if (selectedRatio !== 'all') {
                const ratio = getItemRatio(item);
                if (ratio !== selectedRatio) {
                    return false;
                }
            }
            // Search filter (Prompt, model, tags, provider)
            if (search.trim().length > 0) {
                const q = search.trim().toLowerCase();
                const matchPrompt = item.prompt?.toLowerCase().includes(q);
                const matchModel = item.model?.toLowerCase().includes(q);
                const matchProvider = item.provider?.toLowerCase().includes(q);
                const matchTags = item.tags?.some((t) => t.toLowerCase().includes(q));
                if (!matchPrompt && !matchModel && !matchProvider && !matchTags) {
                    return false;
                }
            }
            // Workspace filter
            if (onlyCurrentWorkspace && activeWorkspace) {
                if (!isItemInWorkspace(item, activeWorkspace)) {
                    return false;
                }
            }
            return true;
        })
            .sort((a, b) => {
            if (sortBy === 'oldest') {
                return a.createdAt - b.createdAt;
            }
            return b.createdAt - a.createdAt;
        });
    }, [items, activeTab, selectedProvider, selectedRatio, search, sortBy, onlyCurrentWorkspace, activeWorkspace]);
    // Count of items belonging to the current workspace (before other search/filter criteria)
    const workspaceItemsCount = useMemo(() => {
        if (!activeWorkspace)
            return items.length;
        return items.filter((item) => isItemInWorkspace(item, activeWorkspace)).length;
    }, [items, activeWorkspace]);
    // Current item index in the active filtered sequence
    const currentPreviewIndex = useMemo(() => {
        if (!previewItem)
            return -1;
        return filteredItems.findIndex((i) => i.id === previewItem.id);
    }, [previewItem, filteredItems]);
    const hasPrev = currentPreviewIndex > 0;
    const hasNext = currentPreviewIndex >= 0 && currentPreviewIndex < filteredItems.length - 1;
    // Safely open or navigate preview item
    const openPreviewItem = (item, blob) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        setPreviewItem(item);
        if (blob) {
            blobCache.set(item.id, blob);
            const url = URL.createObjectURL(blob);
            previewUrlRef.current = url;
            setPreviewBlob(blob);
            setPreviewUrl(url);
            setPreviewLoading(false);
            return;
        }
        const cached = blobCache.get(item.id);
        if (cached) {
            const url = URL.createObjectURL(cached);
            previewUrlRef.current = url;
            setPreviewBlob(cached);
            setPreviewUrl(url);
            setPreviewLoading(false);
            return;
        }
        setPreviewLoading(true);
        setPreviewBlob(null);
        setPreviewUrl(null);
        const controller = new AbortController();
        abortControllerRef.current = controller;
        fetchAttachmentBlob(item.attachment)
            .then((fetchedBlob) => {
            if (controller.signal.aborted)
                return;
            blobCache.set(item.id, fetchedBlob);
            const url = URL.createObjectURL(fetchedBlob);
            previewUrlRef.current = url;
            setPreviewBlob(fetchedBlob);
            setPreviewUrl(url);
            setPreviewLoading(false);
        })
            .catch((err) => {
            if (controller.signal.aborted)
                return;
            setPreviewLoading(false);
            showToast(t('copyFailed'));
        });
    };
    // Close preview and revoke object URL
    const handleClosePreview = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        if (regenerateControllerRef.current) {
            regenerateControllerRef.current.abort();
            regenerateControllerRef.current = null;
            setIsRegenerating(false);
        }
        setShowRegenerateModal(false);
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        setPreviewItem(null);
        setPreviewUrl(null);
        setPreviewBlob(null);
        setPreviewLoading(false);
    };
    const goToPrev = () => {
        if (currentPreviewIndex > 0) {
            const prevItem = filteredItems[currentPreviewIndex - 1];
            if (prevItem)
                openPreviewItem(prevItem);
        }
    };
    const goToNext = () => {
        if (currentPreviewIndex >= 0 && currentPreviewIndex < filteredItems.length - 1) {
            const nextItem = filteredItems[currentPreviewIndex + 1];
            if (nextItem)
                openPreviewItem(nextItem);
        }
    };
    const handleOpenRegenerate = () => {
        if (!previewItem || isRegenerating)
            return;
        setRegeneratePrompt(previewItem.prompt);
        setShowRegenerateModal(true);
    };
    const handleCancelRegeneratePrompt = () => {
        setShowRegenerateModal(false);
    };
    const handleAbortRegenerating = () => {
        regenerateControllerRef.current?.abort();
        regenerateControllerRef.current = null;
        setIsRegenerating(false);
        showToast(t('cancel'));
    };
    const handleConfirmRegenerate = async () => {
        if (!previewItem || isRegenerating || regeneratePrompt.trim().length === 0)
            return;
        setShowRegenerateModal(false);
        setIsRegenerating(true);
        const controller = new AbortController();
        regenerateControllerRef.current = controller;
        try {
            const request = conversationRegenerateRequest(previewItem, regeneratePrompt, previewItem.aspectRatio
                ? { ratio: previewItem.aspectRatio, quality: previewItem.imageSize || 'standard' }
                : undefined);
            if (activeWorkspace?.path) {
                request.workspaceRoot = activeWorkspace.path;
            }
            const response = await fetch(STUDIO_ROUTE, {
                method: 'POST',
                credentials: 'same-origin',
                signal: controller.signal,
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify(request),
            });
            const payload = (await response.json().catch(() => null));
            if (!response.ok || payload === null || !('attachment' in payload)) {
                throw new Error(payload && 'error' in payload && payload.error ? payload.error : t('regenerateFailed'));
            }
            const newItem = {
                id: String(payload.attachment.attachmentId),
                attachment: payload.attachment,
                prompt: payload.prompt,
                provider: payload.provider,
                model: payload.model,
                createdAt: payload.createdAt,
                aspectRatio: request.ratio,
                imageSize: request.quality,
                output: payload.output,
                ...(payload.savedTo ? { savedTo: payload.savedTo } : {}),
                ...(activeWorkspace?.path ? { workspacePath: activeWorkspace.path } : {}),
                ...(activeWorkspace?.workspaceId ? { workspaceId: activeWorkspace.workspaceId } : {}),
                ...(currentSessionId ? { sessionId: currentSessionId } : {}),
            };
            const savedOk = await saveGalleryItem(newItem);
            if (activeTab !== 'gallery') {
                setActiveTab('gallery');
            }
            if (search.trim().length > 0) {
                setSearch('');
            }
            // Smoothly update preview to the newly created item and load its image blob
            openPreviewItem(newItem);
            showToast(savedOk ? t('regenerateSuccess') : t('regenerateSaveFailed'));
        }
        catch (err) {
            if (controller.signal.aborted)
                return;
            showToast(err instanceof Error ? err.message : String(err));
        }
        finally {
            setIsRegenerating(false);
            regenerateControllerRef.current = null;
        }
    };
    // Clean up only on component unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current)
                abortControllerRef.current.abort();
            if (regenerateControllerRef.current)
                regenerateControllerRef.current.abort();
            if (previewUrlRef.current)
                URL.revokeObjectURL(previewUrlRef.current);
        };
    }, []);
    // Keyboard navigation: Esc (close modal or preview), ArrowLeft (prev), ArrowRight (next)
    useEffect(() => {
        const onKeyDown = (e) => {
            if (['INPUT', 'TEXTAREA'].includes(e.target?.tagName)) {
                return;
            }
            if (showRegenerateModal) {
                if (e.key === 'Escape') {
                    e.preventDefault();
                    setShowRegenerateModal(false);
                }
                return;
            }
            if (showDeleteModal) {
                if (e.key === 'Escape' && !isDeleting) {
                    e.preventDefault();
                    setShowDeleteModal(false);
                    if (!isManageMode)
                        setSelectedIds(new Set());
                }
                return;
            }
            if (previewItem) {
                if (e.key === 'Escape') {
                    handleClosePreview();
                }
                else if (e.key === 'ArrowLeft' || e.key === 'Left') {
                    e.preventDefault();
                    goToPrev();
                }
                else if (e.key === 'ArrowRight' || e.key === 'Right') {
                    e.preventDefault();
                    goToNext();
                }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [showRegenerateModal, showDeleteModal, isDeleting, isManageMode, previewItem, currentPreviewIndex, filteredItems]);
    // Shift-Click Range Selection & Card Click Handler
    const handleCardClick = (item, index, e, blob) => {
        // If holding Shift, or if currently in manage mode:
        if (e.shiftKey || isManageMode) {
            e.stopPropagation();
            if (!isManageMode) {
                setIsManageMode(true);
            }
            if (e.shiftKey && lastSelectedIndexRef.current !== null) {
                const from = Math.min(lastSelectedIndexRef.current, index);
                const to = Math.max(lastSelectedIndexRef.current, index);
                const rangeItems = filteredItems.slice(from, to + 1);
                setSelectedIds((prev) => {
                    const next = new Set(prev);
                    for (const r of rangeItems) {
                        next.add(r.id);
                    }
                    return next;
                });
            }
            else {
                setSelectedIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(item.id)) {
                        next.delete(item.id);
                    }
                    else {
                        next.add(item.id);
                    }
                    return next;
                });
                lastSelectedIndexRef.current = index;
            }
            return;
        }
        // Normal click -> open preview
        if (blob) {
            openPreviewItem(item, blob);
        }
    };
    // Toggle selection for a specific card index
    const handleToggleSelect = (id, index, e) => {
        e.stopPropagation();
        if (!isManageMode) {
            setIsManageMode(true);
        }
        if (e.shiftKey && lastSelectedIndexRef.current !== null) {
            const from = Math.min(lastSelectedIndexRef.current, index);
            const to = Math.max(lastSelectedIndexRef.current, index);
            const rangeItems = filteredItems.slice(from, to + 1);
            setSelectedIds((prev) => {
                const next = new Set(prev);
                for (const r of rangeItems) {
                    next.add(r.id);
                }
                return next;
            });
        }
        else {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                }
                else {
                    next.add(id);
                }
                return next;
            });
            lastSelectedIndexRef.current = index;
        }
    };
    const handleSelectAll = () => {
        setSelectedIds(new Set(filteredItems.map((i) => i.id)));
    };
    const handleInvertSelect = () => {
        setSelectedIds((prev) => {
            const next = new Set();
            for (const item of filteredItems) {
                if (!prev.has(item.id)) {
                    next.add(item.id);
                }
            }
            return next;
        });
    };
    const handleClearSelect = () => {
        setSelectedIds(new Set());
        lastSelectedIndexRef.current = null;
    };
    const handleExitManage = () => {
        setIsManageMode(false);
        setSelectedIds(new Set());
        lastSelectedIndexRef.current = null;
    };
    // Selected items array
    const selectedItems = useMemo(() => {
        if (selectedIds.size === 0)
            return [];
        return items.filter((item) => selectedIds.has(item.id));
    }, [items, selectedIds]);
    // Whether all selected items are currently favorited
    const allSelectedAreFavorites = useMemo(() => {
        if (selectedItems.length === 0)
            return false;
        return selectedItems.every((item) => Boolean(item.isFavorite));
    }, [selectedItems]);
    // Batch favorite toggle handler (Optimistic UI + atomic IndexedDB transaction)
    const handleBatchToggleFavorite = async () => {
        if (selectedIds.size === 0)
            return;
        const targetFavorite = !allSelectedAreFavorites;
        const targetIds = Array.from(selectedIds);
        // 1. Optimistic UI update
        setItems((prev) => prev.map((item) => (selectedIds.has(item.id) ? { ...item, isFavorite: targetFavorite } : item)));
        // 2. Persist in IndexedDB in one transaction
        try {
            await bulkSetFavoriteGalleryItems(targetIds, targetFavorite);
            showToast(targetFavorite
                ? t('batchFavoritedToast', { count: String(targetIds.length) })
                : t('batchUnfavoritedToast', { count: String(targetIds.length) }));
        }
        catch (err) {
            console.error('[dsh-image-gen] Batch favorite failed:', err);
            void getGalleryItems().then((res) => setItems(res));
            showToast(t('batchFavoriteFailed'));
        }
    };
    // Batch download state
    const [isDownloadingBatch, setIsDownloadingBatch] = useState(false);
    const [batchDownloadProgress, setBatchDownloadProgress] = useState(null);
    // Batch download handler (single PNG or bundled zero-compression ZIP)
    const handleBatchDownload = async () => {
        if (selectedIds.size === 0 || isDownloadingBatch)
            return;
        const itemsToDownload = selectedItems;
        if (itemsToDownload.length === 0)
            return;
        setIsDownloadingBatch(true);
        try {
            // Single image: direct download
            if (itemsToDownload.length === 1) {
                const single = itemsToDownload[0];
                if (!single)
                    return;
                let blob = blobCache.get(single.id);
                if (!blob) {
                    blob = await fetchAttachmentBlob(single.attachment);
                    blobCache.set(single.id, blob);
                }
                const objectUrl = URL.createObjectURL(blob);
                downloadBlobUrl(objectUrl, `dsh-${single.provider}-${single.id}.png`);
                URL.revokeObjectURL(objectUrl);
                showToast(t('batchDownloadSingleToast'));
                return;
            }
            // Multiple images: bundle into ZIP without multi-file browser download blocks
            const zipFiles = [];
            const usedNames = new Set();
            for (let i = 0; i < itemsToDownload.length; i++) {
                const it = itemsToDownload[i];
                if (!it)
                    continue;
                setBatchDownloadProgress(`${i + 1}/${itemsToDownload.length}`);
                let blob = blobCache.get(it.id);
                if (!blob) {
                    blob = await fetchAttachmentBlob(it.attachment);
                    blobCache.set(it.id, blob);
                }
                const arrayBuffer = await blob.arrayBuffer();
                const ext = blob.type === 'image/jpeg' ? 'jpg' : blob.type === 'image/webp' ? 'webp' : 'png';
                let baseName = `dsh-${it.provider}-${it.id.slice(0, 8)}.${ext}`;
                let count = 1;
                while (usedNames.has(baseName)) {
                    baseName = `dsh-${it.provider}-${it.id.slice(0, 8)}-${count}.${ext}`;
                    count++;
                }
                usedNames.add(baseName);
                zipFiles.push({
                    name: baseName,
                    data: new Uint8Array(arrayBuffer),
                });
            }
            const zipBlob = createZipBlob(zipFiles);
            const zipUrl = URL.createObjectURL(zipBlob);
            const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            downloadBlobUrl(zipUrl, `dsh-gallery-${dateStr}.zip`);
            URL.revokeObjectURL(zipUrl);
            showToast(t('batchDownloadZipToast', { count: String(itemsToDownload.length) }));
        }
        catch (err) {
            console.error('[dsh-image-gen] Batch download failed:', err);
            showToast(t('batchDownloadFailed'));
        }
        finally {
            setIsDownloadingBatch(false);
            setBatchDownloadProgress(null);
        }
    };
    // Request single image deletion through the unified modal
    const requestSingleDelete = (item, e) => {
        if (e)
            e.stopPropagation();
        setSelectedIds(new Set([item.id]));
        setShowDeleteModal(true);
    };
    const hasWorkspaceFilesToDelete = useMemo(() => items.some(i => selectedIds.has(i.id) && typeof i.savedTo === 'string' && i.savedTo.trim().length > 0), [items, selectedIds]);
    // Execute batch deletion (IndexedDB single transaction + optional workspace disk unlink)
    const handleConfirmBatchDelete = async () => {
        if (selectedIds.size === 0 || isDeleting)
            return;
        setIsDeleting(true);
        const idsToDelete = Array.from(selectedIds);
        const itemsToDelete = items.filter((i) => selectedIds.has(i.id));
        // 1. Collect exact workspace file paths
        const pathsToDelete = itemsToDelete
            .map((i) => i.savedTo)
            .filter((p) => typeof p === 'string' && p.trim().length > 0);
        let deletedFiles = 0;
        if (deleteWorkspaceFiles && pathsToDelete.length > 0) {
            try {
                const res = await fetch(DELETE_ROUTE, {
                    method: 'POST',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({
                        paths: pathsToDelete,
                    }),
                });
                if (!res.ok) {
                    setIsDeleting(false);
                    showToast(`工作区文件删除失败 (${res.status})`);
                    return;
                }
                const data = (await res.json().catch(() => null));
                if (!data || data.ok === false || (Array.isArray(data.failedFiles) && data.failedFiles.length > 0)) {
                    setIsDeleting(false);
                    const reason = data?.failedFiles?.[0]?.error || '工作区文件未能删除';
                    showToast(`删除失败：${reason}`);
                    return;
                }
                deletedFiles = data.deletedCount ?? 0;
            }
            catch (err) {
                setIsDeleting(false);
                showToast(err instanceof Error ? err.message : '工作区文件删除网络异常');
                return;
            }
        }
        // 2. Perform IndexedDB deletion with real error handling
        try {
            await bulkDeleteGalleryItems(idsToDelete);
            for (const item of itemsToDelete) {
                evictAttachmentCache(item.attachment.attachmentId);
            }
        }
        catch (err) {
            setIsDeleting(false);
            showToast(t('deleteFailedDatabase'));
            return;
        }
        // Close lightbox if preview item was deleted
        if (previewItem && selectedIds.has(previewItem.id)) {
            handleClosePreview();
        }
        setIsDeleting(false);
        setShowDeleteModal(false);
        if (isManageMode) {
            handleExitManage();
        }
        else {
            setSelectedIds(new Set());
            lastSelectedIndexRef.current = null;
        }
        if (deletedFiles > 0) {
            showToast(t('batchDeletedWithFilesToast', { count: String(idsToDelete.length), files: String(deletedFiles) }));
        }
        else {
            showToast(t('batchDeletedToast', { count: String(idsToDelete.length) }));
        }
    };
    return (_jsxs("div", { className: `dsh-ig-gallery-page${inSidebar ? ' dsh-ig-page-in-sidebar' : ''}`, "data-conversation-composer-overlay": "", children: [_jsxs("header", { className: "dsh-ig-studio-tabs-bar", children: [_jsxs("button", { type: "button", className: `dsh-ig-studio-tab-btn ${activeTab === 'gallery' ? 'is-active' : ''}`, onClick: () => setActiveTab('gallery'), children: [_jsx(ImageIcon, { size: 15 }), _jsx("span", { children: t('tabGallery') })] }), _jsxs("button", { type: "button", className: `dsh-ig-studio-tab-btn ${activeTab === 'studio' ? 'is-active' : ''}`, onClick: () => setActiveTab('studio'), children: [_jsx(SlidersHorizontal, { size: 15 }), _jsx("span", { children: t('tabStudio') })] }), _jsxs("button", { type: "button", className: `dsh-ig-studio-tab-btn ${activeTab === 'inspiration' ? 'is-active' : ''}`, onClick: () => setActiveTab('inspiration'), children: [_jsx(Sparkles, { size: 15 }), _jsx("span", { children: t('tabInspiration') })] }), _jsxs("button", { type: "button", className: `dsh-ig-studio-tab-btn ${activeTab === 'favorites' ? 'is-active' : ''}`, onClick: () => setActiveTab('favorites'), children: [_jsx(Heart, { size: 15 }), _jsx("span", { children: t('tabFavorites') })] })] }), (activeTab === 'gallery' || activeTab === 'favorites') && (_jsxs("div", { className: "dsh-ig-studio-toolbar", children: [_jsxs("div", { className: "dsh-ig-studio-toolbar-left", children: [_jsxs("select", { className: "dsh-ig-studio-select", value: selectedProvider, onChange: (e) => setSelectedProvider(e.target.value), children: [_jsx("option", { value: "all", children: t('filterAllProviders') }), _jsx("option", { value: "google", children: t('filterGoogle') }), _jsx("option", { value: "openai", children: t('filterOpenAI') }), _jsx("option", { value: "openai-compat", children: t('filterOpenAICompat') }), _jsx("option", { value: "seedream", children: t('filterSeedream') }), _jsx("option", { value: "dashscope", children: t('filterDashScope') }), _jsx("option", { value: "xai", children: t('filterXAI') }), _jsx("option", { value: "zhipu", children: t('filterZhipu') }), _jsx("option", { value: "comfyui", children: t('filterComfyUI') })] }), _jsxs("select", { className: "dsh-ig-studio-select", value: selectedRatio, onChange: (e) => setSelectedRatio(e.target.value), children: [_jsx("option", { value: "all", children: t('filterAllRatios') }), _jsx("option", { value: "1:1", children: "1:1" }), _jsx("option", { value: "16:9", children: "16:9" }), _jsx("option", { value: "9:16", children: "9:16" }), _jsx("option", { value: "4:3", children: "4:3" }), _jsx("option", { value: "3:4", children: "3:4" }), _jsx("option", { value: "3:2", children: "3:2" }), _jsx("option", { value: "2:3", children: "2:3" })] }), _jsxs("div", { className: "dsh-ig-studio-search-wrap", children: [_jsx(Search, { className: "dsh-ig-studio-search-icon", size: 14 }), _jsx("input", { type: "text", className: "dsh-ig-studio-search-input", placeholder: t('searchPlaceholder'), value: search, onChange: (e) => setSearch(e.target.value) })] }), _jsxs("label", { className: "dsh-ig-workspace-filter-label", title: t('filterCurrentWorkspaceHint'), children: [_jsx("input", { type: "checkbox", className: "dsh-ig-workspace-checkbox", checked: onlyCurrentWorkspace, onChange: (e) => setOnlyCurrentWorkspace(e.target.checked) }), _jsx("span", { className: "dsh-ig-workspace-filter-text", children: t('filterCurrentWorkspace') }), _jsxs("span", { className: "dsh-ig-workspace-filter-badge", children: [workspaceItemsCount, "/", items.length] })] })] }), _jsxs("div", { className: "dsh-ig-studio-toolbar-right", children: [_jsxs("select", { className: "dsh-ig-studio-select dsh-ig-studio-select-sort", value: sortBy, onChange: (e) => setSortBy(e.target.value), children: [_jsx("option", { value: "newest", children: t('sortNewest') }), _jsx("option", { value: "oldest", children: t('sortOldest') })] }), _jsxs("button", { type: "button", className: `dsh-ig-studio-btn ${isManageMode ? 'is-active' : ''}`, title: isManageMode ? t('exitManage') : t('manage'), onClick: () => {
                                    if (isManageMode) {
                                        handleExitManage();
                                    }
                                    else {
                                        setIsManageMode(true);
                                    }
                                }, children: [_jsx(CheckSquare, { size: 13 }), _jsx("span", { children: isManageMode ? t('exitManage') : t('manage') })] })] })] })), _jsx("div", { className: `dsh-ig-gallery-page-body ${activeTab === 'studio' ? 'is-workbench' : ''}`, children: activeTab === 'gallery' || activeTab === 'favorites' ? (items.length === 0 ? (_jsxs("div", { className: "dsh-ig-gallery-empty", children: [_jsx("div", { className: "dsh-ig-gallery-empty-icon", children: "\uD83D\uDDBC\uFE0F" }), _jsx("div", { className: "dsh-ig-gallery-empty-title", children: t('emptyTitle') }), _jsx("div", { className: "dsh-ig-gallery-empty-desc", children: t('emptyDesc') })] })) : activeTab === 'favorites' && filteredItems.length === 0 && search === '' && selectedProvider === 'all' && selectedRatio === 'all' ? (_jsxs("div", { className: "dsh-ig-gallery-empty", children: [_jsx("div", { className: "dsh-ig-gallery-empty-icon", children: "\uD83E\uDD0D" }), _jsx("div", { className: "dsh-ig-gallery-empty-title", children: t('favEmptyTitle') }), _jsx("div", { className: "dsh-ig-gallery-empty-desc", children: t('favEmptyDesc') })] })) : filteredItems.length === 0 ? (_jsxs("div", { className: "dsh-ig-gallery-empty", children: [_jsx("div", { className: "dsh-ig-gallery-empty-icon", children: "\uD83D\uDD0D" }), _jsx("div", { className: "dsh-ig-gallery-empty-title", children: t('noMatchTitle') }), _jsx("div", { className: "dsh-ig-gallery-empty-desc", children: t('noMatchDesc') })] })) : (_jsx("div", { className: "dsh-ig-gallery-grid", children: filteredItems.map((item, idx) => (_jsx(GalleryCard, { item: item, index: idx, isManageMode: isManageMode, isSelected: selectedIds.has(item.id), t: t, onClick: (e, blob) => handleCardClick(item, idx, e, blob), onToggleSelect: (e) => handleToggleSelect(item.id, idx, e), onRequestDelete: () => requestSingleDelete(item), onPreview: (blob) => openPreviewItem(item, blob), onBlobLoaded: (b) => blobCache.set(item.id, b), onToast: showToast }, item.id))) }))) : activeTab === 'inspiration' ? (_jsx(InspirationView, { locale: locale, onUsePrompt: useInspirationPrompt })) : (_jsx(StudioView, { locale: locale, credentialEvents: credentialEvents, workspace: activeWorkspace, initialPrompt: studioDraft, initialCanvasSurface: initialCanvasSurface, showInfiniteCanvasHint: !inSidebar, onInitialPromptApplied: clearStudioDraft, onOpenInspiration: () => setActiveTab('inspiration') })) }), toast && _jsx("div", { className: "dsh-ig-gallery-page-toast", children: toast }), (isManageMode || selectedIds.size > 0) && (_jsxs("div", { className: "dsh-ig-batch-bar", children: [_jsxs("div", { className: "dsh-ig-batch-bar-left", children: [_jsx("span", { className: "dsh-ig-batch-counter", children: t('selectedCount', { n: String(selectedIds.size) }) }), _jsx("button", { type: "button", className: "dsh-ig-batch-btn", onClick: handleSelectAll, children: t('selectAll') }), _jsx("button", { type: "button", className: "dsh-ig-batch-btn", onClick: handleInvertSelect, children: t('invertSelect') }), selectedIds.size > 0 && (_jsx("button", { type: "button", className: "dsh-ig-batch-btn", onClick: handleClearSelect, children: t('clearSelect') }))] }), _jsxs("div", { className: "dsh-ig-batch-bar-right", children: [_jsxs("button", { type: "button", className: `dsh-ig-batch-btn ${allSelectedAreFavorites ? 'is-favorited' : ''}`, disabled: selectedIds.size === 0, onClick: () => void handleBatchToggleFavorite(), title: allSelectedAreFavorites ? t('batchUnfavorite') : t('batchFavorite'), children: [_jsx(Heart, { size: 13, fill: allSelectedAreFavorites ? '#ef4444' : 'none', color: allSelectedAreFavorites ? '#ef4444' : 'currentColor' }), _jsx("span", { children: allSelectedAreFavorites ? t('batchUnfavorite') : t('batchFavorite') })] }), _jsxs("button", { type: "button", className: "dsh-ig-batch-btn dsh-ig-batch-btn-download", disabled: selectedIds.size === 0 || isDownloadingBatch, onClick: () => void handleBatchDownload(), title: t('batchDownload'), children: [_jsx(Download, { size: 13 }), _jsx("span", { children: isDownloadingBatch && batchDownloadProgress
                                            ? t('batchDownloading', {
                                                current: batchDownloadProgress.split('/')[0] ?? '',
                                                total: batchDownloadProgress.split('/')[1] ?? '',
                                            })
                                            : t('batchDownload') })] }), _jsx("span", { className: "dsh-ig-batch-divider" }), _jsxs("button", { type: "button", className: "dsh-ig-batch-btn dsh-ig-batch-btn-danger", disabled: selectedIds.size === 0, onClick: () => setShowDeleteModal(true), children: [_jsx(Trash2, { size: 13 }), _jsxs("span", { children: [t('batchDelete'), selectedIds.size > 0 ? ` (${selectedIds.size})` : ''] })] }), _jsx("button", { type: "button", className: "dsh-ig-batch-btn dsh-ig-batch-btn-exit", onClick: handleExitManage, children: t('exitManage') })] })] })), showDeleteModal && (_jsx("div", { className: "dsh-ig-modal-backdrop", onClick: () => {
                    if (!isDeleting) {
                        setShowDeleteModal(false);
                        if (!isManageMode)
                            setSelectedIds(new Set());
                    }
                }, children: _jsxs("div", { className: "dsh-ig-modal-box", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "dsh-ig-modal-header", children: [_jsx("div", { className: "dsh-ig-modal-icon-danger", children: _jsx(AlertTriangle, { size: 20 }) }), _jsx("div", { className: "dsh-ig-modal-title", children: selectedIds.size === 1
                                        ? t('batchDeleteTitleSingle')
                                        : t('batchDeleteTitle', { count: String(selectedIds.size) }) })] }), _jsxs("div", { className: "dsh-ig-modal-body", children: [_jsx("p", { className: "dsh-ig-modal-desc", children: t('batchDeleteDesc') }), hasWorkspaceFilesToDelete && (_jsxs("label", { className: "dsh-ig-modal-checkbox-label", children: [_jsx("input", { type: "checkbox", checked: deleteWorkspaceFiles, onChange: (e) => setDeleteWorkspaceFiles(e.target.checked), disabled: isDeleting }), _jsx("span", { children: t('deleteWorkspaceFilesOpt') })] }))] }), _jsxs("div", { className: "dsh-ig-modal-footer", children: [_jsx("button", { type: "button", className: "dsh-ig-modal-btn dsh-ig-modal-btn-cancel", onClick: () => {
                                        setShowDeleteModal(false);
                                        if (!isManageMode)
                                            setSelectedIds(new Set());
                                    }, disabled: isDeleting, children: t('cancel') }), _jsx("button", { type: "button", className: "dsh-ig-modal-btn dsh-ig-modal-btn-danger", onClick: handleConfirmBatchDelete, disabled: isDeleting, children: isDeleting
                                        ? '...'
                                        : selectedIds.size === 1
                                            ? t('confirmDeleteSingle')
                                            : t('confirmBatchDelete', { count: String(selectedIds.size) }) })] })] }) })), showRegenerateModal && previewItem && (_jsx("div", { className: "dsh-ig-modal-backdrop", onClick: handleCancelRegeneratePrompt, children: _jsxs("div", { className: "dsh-ig-modal-box dsh-ig-regenerate-modal-box", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "dsh-ig-modal-header", children: [_jsx("div", { className: "dsh-ig-regenerate-modal-icon", children: _jsx(Sparkles, { size: 18 }) }), _jsxs("div", { className: "dsh-ig-regenerate-modal-title-wrap", children: [_jsx("div", { className: "dsh-ig-modal-title", children: t('regenerateTitle') }), _jsxs("div", { className: "dsh-ig-regenerate-modal-meta", children: [_jsx("span", { className: "dsh-ig-tag", children: previewItem.provider }), previewItem.model && _jsx("span", { className: "dsh-ig-tag dsh-ig-tag-model", children: previewItem.model }), _jsx("span", { className: "dsh-ig-tag", children: formatCardMeta(previewItem) })] })] })] }), _jsxs("div", { className: "dsh-ig-modal-body", children: [_jsx("p", { className: "dsh-ig-modal-desc", children: t('regenerateHint') }), _jsx("textarea", { className: "dsh-ig-regenerate-modal-textarea", value: regeneratePrompt, onChange: (e) => setRegeneratePrompt(e.target.value), placeholder: t('prompt'), rows: 4, autoFocus: true })] }), _jsxs("div", { className: "dsh-ig-modal-footer", children: [_jsx("button", { type: "button", className: "dsh-ig-modal-btn dsh-ig-modal-btn-cancel", onClick: handleCancelRegeneratePrompt, children: t('cancel') }), _jsxs("button", { type: "button", className: "dsh-ig-modal-btn dsh-ig-modal-btn-primary", onClick: handleConfirmRegenerate, disabled: regeneratePrompt.trim().length === 0, children: [_jsx(Sparkles, { size: 14 }), _jsx("span", { children: t('confirmRegenerate') })] })] })] }) })), previewItem && (_jsxs("div", { className: "dsh-ig-lightbox-backdrop", onClick: handleClosePreview, children: [_jsxs("div", { className: "dsh-ig-lightbox-topbar", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "dsh-ig-lightbox-meta", children: [_jsx("span", { className: "dsh-ig-tag", children: previewItem.provider }), previewItem.model ? (_jsx("span", { className: "dsh-ig-tag dsh-ig-tag-model", children: previewItem.model })) : null, currentPreviewIndex >= 0 && (_jsxs("span", { className: "dsh-ig-lightbox-counter", children: [currentPreviewIndex + 1, " / ", filteredItems.length] }))] }), _jsx("button", { type: "button", className: "dsh-ig-lightbox-close-btn", title: t('close'), onClick: handleClosePreview, children: _jsx(X, { size: 18 }) })] }), _jsx("button", { type: "button", className: "dsh-ig-lightbox-nav-btn dsh-ig-lightbox-nav-prev", title: t('prevImage'), disabled: !hasPrev, onClick: (e) => {
                            e.stopPropagation();
                            goToPrev();
                        }, children: _jsx(ChevronLeft, { size: 24 }) }), _jsx("button", { type: "button", className: "dsh-ig-lightbox-nav-btn dsh-ig-lightbox-nav-next", title: t('nextImage'), disabled: !hasNext, onClick: (e) => {
                            e.stopPropagation();
                            goToNext();
                        }, children: _jsx(ChevronRight, { size: 24 }) }), _jsx("div", { className: "dsh-ig-lightbox-img-wrap", onClick: (e) => e.stopPropagation(), children: previewLoading ? (_jsx("div", { className: "dsh-ig-lightbox-loading", children: _jsx("div", { className: "dsh-ig-lightbox-spinner" }) })) : previewUrl ? (_jsx("img", { className: "dsh-ig-lightbox-img", src: previewUrl, alt: previewItem.prompt })) : null }), _jsxs("div", { className: "dsh-ig-lightbox-bottombar", onClick: (e) => e.stopPropagation(), children: [_jsx("div", { className: "dsh-ig-lightbox-prompt-text", title: previewItem.prompt, children: previewItem.prompt }), _jsxs("div", { className: "dsh-ig-lightbox-actions", children: [_jsxs("button", { type: "button", className: "dsh-ig-lightbox-btn", title: t('copyPpt'), onClick: async () => {
                                            await navigator.clipboard.writeText(previewItem.prompt);
                                            showToast(t('copiedPrompt'));
                                        }, children: [_jsx(FileText, { size: 14 }), _jsx("span", { children: t('copyPpt') })] }), _jsxs("button", { type: "button", className: "dsh-ig-lightbox-btn", title: t('copyImg'), onClick: async () => {
                                            if (!previewBlob)
                                                return;
                                            const ok = await copyImageBlob(previewBlob);
                                            showToast(ok ? t('copiedImage') : t('copyFailed'));
                                        }, children: [_jsx(Copy, { size: 14 }), _jsx("span", { children: t('copyImg') })] }), _jsxs("button", { type: "button", className: "dsh-ig-lightbox-btn", title: t('download'), onClick: () => {
                                            if (!previewUrl)
                                                return;
                                            const a = document.createElement('a');
                                            a.href = previewUrl;
                                            a.download = `dsh-${previewItem.provider}-${previewItem.id}.png`;
                                            document.body.appendChild(a);
                                            a.click();
                                            document.body.removeChild(a);
                                        }, children: [_jsx(Download, { size: 14 }), _jsx("span", { children: t('download') })] }), _jsxs("button", { type: "button", className: "dsh-ig-lightbox-btn dsh-ig-lightbox-btn-regenerate", title: t('regenerate'), disabled: isRegenerating, onClick: handleOpenRegenerate, children: [_jsx(Sparkles, { size: 14 }), _jsx("span", { children: t('regenerate') })] }), isRegenerating && (_jsxs("div", { className: "dsh-ig-lightbox-generating-indicator", children: [_jsx("div", { className: "dsh-ig-lightbox-spinner-sm" }), _jsx("span", { children: t('regenerating') }), _jsx("button", { type: "button", className: "dsh-ig-lightbox-abort-btn", onClick: handleAbortRegenerating, children: t('cancel') })] })), _jsxs("button", { type: "button", className: "dsh-ig-lightbox-btn dsh-ig-lightbox-btn-danger", title: t('delete'), onClick: () => {
                                            if (previewItem) {
                                                requestSingleDelete(previewItem);
                                            }
                                        }, children: [_jsx(Trash2, { size: 14 }), _jsx("span", { children: t('delete') })] })] })] })] }))] }));
};
const GalleryCard = ({ item, index: _index, isManageMode, isSelected, t, onClick, onToggleSelect, onRequestDelete, onPreview: _onPreview, onBlobLoaded, onToast, }) => {
    const [url, setUrl] = useState();
    const [blob, setBlob] = useState();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    useEffect(() => {
        const controller = new AbortController();
        let objectUrl;
        void fetch(IMAGE_ROUTE, {
            method: 'POST',
            signal: controller.signal,
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ attachment: item.attachment }),
        })
            .then(async (response) => {
            if (!response.ok)
                throw new Error(`HTTP ${response.status}`);
            const resBlob = await response.blob();
            if (controller.signal.aborted)
                return;
            setBlob(resBlob);
            onBlobLoaded?.(resBlob);
            objectUrl = URL.createObjectURL(resBlob);
            setUrl(objectUrl);
            setLoading(false);
        })
            .catch((err) => {
            if (!controller.signal.aborted) {
                setError(err instanceof Error ? err.message : String(err));
                setLoading(false);
            }
        });
        return () => {
            controller.abort();
            if (objectUrl)
                URL.revokeObjectURL(objectUrl);
        };
    }, [item.attachment]);
    const copyPrompt = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(item.prompt);
            onToast(t('copiedPrompt'));
        }
        catch {
            onToast(t('copyFailed'));
        }
    };
    const copyImage = async (e) => {
        e.stopPropagation();
        if (!blob)
            return;
        const ok = await copyImageBlob(blob);
        onToast(ok ? t('copiedImage') : t('copyFailed'));
    };
    const downloadImage = (e) => {
        e.stopPropagation();
        if (!url)
            return;
        const a = document.createElement('a');
        a.href = url;
        a.download = `dsh-${item.provider}-${item.id}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };
    const toggleFavorite = async (e) => {
        e.stopPropagation();
        const nextStatus = await toggleFavoriteGalleryItem(item.id);
        onToast(nextStatus ? t('favoriteAdded') : t('favoriteRemoved'));
    };
    // Label for top badge: show model or fallback to provider
    const badgeLabel = item.model || item.provider;
    return (_jsxs("div", { className: `dsh-ig-gallery-card ${isSelected ? 'is-selected' : ''} ${isManageMode ? 'is-manage-mode' : ''}`, onClick: (e) => onClick(e, blob), children: [_jsxs("div", { className: "dsh-ig-gallery-card-media", children: [_jsx("button", { type: "button", className: `dsh-ig-card-checkbox ${isSelected ? 'is-checked' : ''}`, title: isSelected ? t('clearSelect') : t('manage'), onClick: onToggleSelect, children: isSelected && _jsx(Check, { size: 11, strokeWidth: 3 }) }), loading && _jsx("div", { className: "dsh-ig-gallery-card-loading", children: "..." }), error && _jsxs("div", { className: "dsh-ig-gallery-card-error", children: ["\u26A0\uFE0F ", error] }), url && (_jsx("img", { className: "dsh-ig-gallery-card-img", src: url, alt: item.prompt, loading: "lazy" })), _jsxs("div", { className: "dsh-ig-card-toolbar", children: [_jsx("button", { type: "button", className: "dsh-ig-tool-btn", title: t('copyImg'), onClick: (e) => {
                                    void copyImage(e);
                                }, children: _jsx(Copy, { size: 13 }) }), _jsx("button", { type: "button", className: "dsh-ig-tool-btn", title: t('download'), onClick: downloadImage, children: _jsx(Download, { size: 13 }) }), _jsx("button", { type: "button", className: "dsh-ig-tool-btn", title: t('copyPpt'), onClick: (e) => {
                                    void copyPrompt(e);
                                }, children: _jsx(FileText, { size: 13 }) }), _jsx("button", { type: "button", className: "dsh-ig-tool-btn dsh-ig-tool-btn-danger", title: t('delete'), onClick: (e) => {
                                    e.stopPropagation();
                                    onRequestDelete();
                                }, children: _jsx(Trash2, { size: 13 }) })] })] }), _jsxs("div", { className: "dsh-ig-gallery-card-meta", children: [_jsx("div", { className: "dsh-ig-card-badge-row", children: _jsx("span", { className: "dsh-ig-card-badge", title: badgeLabel, children: badgeLabel }) }), _jsx("div", { className: "dsh-ig-gallery-card-prompt-line", title: item.prompt, children: item.prompt }), _jsxs("div", { className: "dsh-ig-card-footer-row", children: [_jsxs("span", { className: "dsh-ig-card-meta-text", children: [formatCardMeta(item), " | ", formatRelativeTime(item.createdAt, t)] }), _jsx("button", { type: "button", className: `dsh-ig-card-fav-btn ${item.isFavorite ? 'is-favorited' : ''}`, title: item.isFavorite ? t('favoriteRemoved') : t('favoriteAdded'), onClick: (e) => {
                                    void toggleFavorite(e);
                                }, children: _jsx(Heart, { size: 15, fill: item.isFavorite ? 'currentColor' : 'none' }) })] })] })] }));
};
const PlaceholderView = ({ icon, title, description, tip, badge, }) => {
    return (_jsx("div", { className: "dsh-ig-placeholder-view", children: _jsxs("div", { className: "dsh-ig-placeholder-card", children: [_jsx("div", { className: "dsh-ig-placeholder-icon", children: icon }), _jsxs("div", { className: "dsh-ig-placeholder-header", children: [_jsx("h2", { className: "dsh-ig-placeholder-title", children: title }), badge && _jsx("span", { className: "dsh-ig-placeholder-badge", children: badge })] }), _jsx("p", { className: "dsh-ig-placeholder-desc", children: description }), tip && _jsx("div", { className: "dsh-ig-placeholder-tip", children: tip })] }) }));
};
export { copyImageBlob } from './browser-image-utils.js';
//# sourceMappingURL=gallery-view.js.map